import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { AuthShell } from "@/components/AuthShell";
import { AuthDiagPanel } from "@/components/AuthDiagPanel";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { translateApiError } from "@/lib/api";
import { newDiagId, snapshotEnv, snapshotError, pingBackend } from "@/lib/authDiag";

export default function Register() {
  const { t } = useI18n();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [diag, setDiag] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    setDiag(null);
    const diagId = newDiagId();
    try {
      await register(name, email, password, { "X-Auth-Diag-ID": diagId });
      toast.success(t("auth.success.register"));
      navigate("/dashboard");
    } catch (err) {
      const msg = translateApiError(err, t);
      setErrorMsg(msg);
      toast.error(msg);
      const probe = await pingBackend(diagId);
      const env = snapshotEnv();
      const errSnap = snapshotError(err);
      setDiag({ diagId, env, error: errSnap, probe });
      // eslint-disable-next-line no-console
      console.warn("[AUTH-DIAG]", { diagId, env, error: errSnap, probe });
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <AuthShell title={t("auth.register.title")} subtitle={t("auth.register.subtitle")} testId="register-page">
      <form className="space-y-4" onSubmit={onSubmit}>
        <div>
          <Label htmlFor="name">{t("auth.name")}</Label>
          <Input id="name" required minLength={2} value={name} onChange={(e) => setName(e.target.value)}
            className="h-12 mt-1" data-testid="register-name-input" />
        </div>
        <div>
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="h-12 mt-1" data-testid="register-email-input" />
        </div>
        <div>
          <Label htmlFor="password">{t("auth.password")}</Label>
          <Input id="password" type="password" required minLength={6} value={password}
            onChange={(e) => setPassword(e.target.value)} className="h-12 mt-1" data-testid="register-password-input" />
        </div>
        <Button type="submit" size="lg" className="w-full h-12" disabled={loading} data-testid="register-submit-button">
          {loading ? t("common.loading") : t("auth.submit.register")}
        </Button>
        {errorMsg && (
          <div
            role="alert"
            data-testid="register-error-message"
            className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2"
          >
            {errorMsg}
          </div>
        )}
        {diag && <AuthDiagPanel {...diag} />}
      </form>
      <div className="my-6 flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{t("auth.orContinue")}</span>
        <Separator className="flex-1" />
      </div>
      <Button type="button" variant="outline" size="lg" className="w-full h-12" onClick={google} data-testid="register-google-button">
        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden>
          <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.6 4.2-5.5 4.2-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.2.8 3.9 1.5l2.7-2.6C17 3.4 14.7 2.4 12 2.4 6.7 2.4 2.5 6.7 2.5 12s4.2 9.6 9.5 9.6c5.5 0 9.1-3.9 9.1-9.3 0-.6-.1-1.1-.2-1.6H12z"/>
        </svg>
        {t("auth.google")}
      </Button>
      <div className="mt-8 text-center text-sm text-muted-foreground">
        {t("auth.hasAccount")}{" "}
        <Link to="/login" className="text-primary font-medium hover:underline" data-testid="go-to-login">
          {t("nav.login")}
        </Link>
      </div>
    </AuthShell>
  );
}
